import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { API_BASE, getConsent, getAttribution, getFbp, getFbc, newEventId } from "../lib/tracking";

const inputCls =
  "w-full rounded-xl border border-[#C8A6A1]/50 bg-[#F7F2EA] px-4 py-3 text-[#181518] text-sm focus:outline-none focus:ring-2 focus:ring-[#D83A52]/60 placeholder:text-[#181518]/40";

export const LeadFormModal = ({ open, onOpenChange, onSuccess, gate = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    preferenza_contatto: "",
    privacy: false,
  });

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Devi accettare la Privacy Policy per inviare la richiesta.");
      return;
    }
    setLoading(true);
    const eventId = newEventId();
    const marketing = !!(getConsent() && getConsent().marketing);
    try {
      await axios.post(`${API_BASE}/lead`, {
        nome: form.nome,
        cognome: form.cognome,
        email: form.email,
        telefono: form.telefono,
        preferenza_contatto: form.preferenza_contatto,
        marketing_consent: marketing,
        event_id: eventId,
        event_source_url: window.location.href,
        fbp: getFbp(),
        fbc: getFbc(),
        attribution: getAttribution(),
      });
      if (marketing && window.fbq) {
        window.fbq("track", "Lead", {}, { eventID: eventId });
      }
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/grazie");
      }
    } catch {
      toast.error("Si è verificato un errore. Riprova tra qualche istante.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#F7F2EA] border-[#C8A6A1]/40 max-w-lg max-h-[92vh] overflow-y-auto" data-testid="lead-form-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-[#2B1930] text-left">
            {gate ? (
              <>Compila il modulo per <span className="italic text-[#D83A52]">sbloccare il video</span></>
            ) : (
              <>Richiedi ulteriori <span className="italic text-[#D83A52]">informazioni</span></>
            )}
          </DialogTitle>
          {!gate && (
            <DialogDescription className="text-[#181518]/70 text-sm text-left">
              Compila il modulo: ti ricontatteremo telefonicamente o su WhatsApp al più presto.
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input required data-testid="lead-nome-input" className={inputCls} placeholder="Nome *" value={form.nome} onChange={set("nome")} />
            <input required data-testid="lead-cognome-input" className={inputCls} placeholder="Cognome *" value={form.cognome} onChange={set("cognome")} />
          </div>
          <input required type="email" data-testid="lead-email-input" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
          <input required type="tel" data-testid="lead-telefono-input" className={inputCls} placeholder="Numero di telefono *" value={form.telefono} onChange={set("telefono")} />
          {!gate && (
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[#2B1930]/70 mb-2">
                Quando preferisci essere contattato/a? *
              </label>
              <textarea
                required
                data-testid="lead-preferenza-input"
                className={`${inputCls} min-h-[80px] resize-none`}
                placeholder="Es. domani pomeriggio dopo le 15, il lunedì mattina..."
                value={form.preferenza_contatto}
                onChange={set("preferenza_contatto")}
              />
            </div>
          )}
          <label className="flex items-start gap-3 text-xs text-[#181518]/70 leading-relaxed cursor-pointer">
            <input
              type="checkbox"
              data-testid="lead-privacy-checkbox"
              checked={form.privacy}
              onChange={set("privacy")}
              className="mt-0.5 h-4 w-4 accent-[#D83A52]"
            />
            <span>
              Ho letto la{" "}
              <Link to="/privacy-policy" target="_blank" className="underline text-[#D83A52]">
                Privacy Policy
              </Link>{" "}
              e acconsento al trattamento dei miei dati per essere ricontattato/a telefonicamente e via
              WhatsApp e per le finalità di marketing descritte. *
            </span>
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="lead-form-submit-button"
            className="w-full rounded-full bg-[#D83A52] text-white font-bold py-4 text-base hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.98] transition-transform shadow-[0_8px_25px_rgba(216,58,82,0.35)] disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {loading ? "Invio in corso..." : "Invia la richiesta"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
